// The customer's light/dark choice.
//
// Three states, not two: `system` is a real preference, and collapsing it into
// whichever scheme the device happens to be on right now means the portal stops
// following the device when the device changes.
//
// Persisted through StoragePort with the key core defines, so the mobile app
// reads and writes the same setting under the same name.

import { create } from 'zustand';
import {
  COLOR_SCHEME_STORAGE_KEY,
  effectiveScheme,
  isColorSchemePreference,
  type ColorScheme,
  type ColorSchemePreference,
} from '@invoiceflow/portal-core';

import { webStoragePort } from '../services/adapters';
import { getSystemScheme } from '../services/theme';

interface ThemeState {
  preference: ColorSchemePreference;
  systemScheme: ColorScheme;
  /** True once the stored preference has been read back. */
  isHydrated: boolean;

  hydrate: () => Promise<void>;
  setPreference: (preference: ColorSchemePreference) => void;
  setSystemScheme: (scheme: ColorScheme) => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  preference: 'system',
  systemScheme: getSystemScheme(),
  isHydrated: false,

  hydrate: async () => {
    const stored = await webStoragePort.get(COLOR_SCHEME_STORAGE_KEY);
    set({
      preference: isColorSchemePreference(stored) ? stored : 'system',
      isHydrated: true,
    });
  },

  setPreference: preference => {
    set({ preference });
    // Fire-and-forget: the choice is already applied on screen, and a storage
    // failure (private browsing) must not undo it.
    void webStoragePort.set(COLOR_SCHEME_STORAGE_KEY, preference);
  },

  setSystemScheme: scheme => set({ systemScheme: scheme }),
}));

/** The scheme actually in force, once `system` is resolved against the device. */
export function useEffectiveScheme(): ColorScheme {
  const preference = useThemeStore(state => state.preference);
  const systemScheme = useThemeStore(state => state.systemScheme);
  return effectiveScheme(preference, systemScheme);
}

/** Non-reactive read, for code outside React. */
export function currentScheme(): ColorScheme {
  const { preference, systemScheme } = useThemeStore.getState();
  return effectiveScheme(preference, systemScheme);
}
